import { Minus, Plus } from 'lucide-react';
import * as React from 'react';
import { cn } from '../lib/cn';
import { Button } from './button';

export interface StepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label: string;
  decreaseLabel?: string;
  increaseLabel?: string;
  formatValue?: (value: number) => string;
  className?: string;
  size?: 'default' | 'lg';
}

/** Large-touch-target numeric stepper used for servings and timers. */
export function Stepper({
  value,
  onChange,
  min = 1,
  max = 100,
  step = 1,
  label,
  decreaseLabel = 'Decrease',
  increaseLabel = 'Increase',
  formatValue,
  className,
  size = 'default',
}: StepperProps) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(String(value));
  React.useEffect(() => setDraft(String(value)), [value]);

  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  const commit = () => {
    const parsed = Number(draft.replace(',', '.'));
    if (Number.isFinite(parsed)) onChange(clamp(parsed));
    setEditing(false);
  };

  return (
    <div
      className={cn('inline-flex items-center gap-1 rounded-md border bg-background', className)}
      role="group"
      aria-label={label}
    >
      <Button
        type="button"
        variant="ghost"
        size={size === 'lg' ? 'icon-lg' : 'icon'}
        aria-label={decreaseLabel}
        disabled={value <= min}
        onClick={() => onChange(clamp(value - step))}
      >
        <Minus />
      </Button>
      {editing ? (
        <input
          className={cn(
            'w-14 bg-transparent text-center font-semibold tabular-nums outline-none',
            size === 'lg' && 'text-xl',
          )}
          value={draft}
          inputMode="decimal"
          autoFocus
          aria-label={label}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
        />
      ) : (
        <button
          type="button"
          className={cn(
            'min-w-14 px-1 text-center font-semibold tabular-nums',
            size === 'lg' && 'text-xl',
          )}
          onClick={() => setEditing(true)}
          aria-label={`${label}: ${formatValue ? formatValue(value) : value}`}
        >
          {formatValue ? formatValue(value) : value}
        </button>
      )}
      <Button
        type="button"
        variant="ghost"
        size={size === 'lg' ? 'icon-lg' : 'icon'}
        aria-label={increaseLabel}
        disabled={value >= max}
        onClick={() => onChange(clamp(value + step))}
      >
        <Plus />
      </Button>
    </div>
  );
}
